/**
 * PendingApprovalsBadge — top-right floating badge that shows the live
 * count of users awaiting admin approval. Pulses red while ≥1 is
 * pending so it's impossible to miss even when the approval email
 * lands in spam (2026-05-16 added after that exact failure).
 *
 * Polls every 60s. Admin-only. Click jumps to User Management →
 * Pending tab.
 */
import { useEffect, useState, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { UserPlus } from "lucide-react";

const API_URL = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function PendingApprovalsBadge() {
  const [count, setCount] = useState(0);
  const [isAdmin, setIsAdmin] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchCount = useCallback(async () => {
    const token = localStorage.getItem("token");
    if (!token) {
      setCount(0); setIsAdmin(false); return;
    }
    try {
      const r = await axios.get(`${API_URL}/auth/pending-approvals/count`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setCount(r.data?.count || 0);
      // If the backend returned a count > 0 we know we're an admin
      // (non-admins are returned 0). For UI affordance assume admin
      // when the endpoint responds (the route returns {count:0} for
      // non-admins so they see nothing anyway).
      setIsAdmin(true);
    } catch {
      setIsAdmin(false);
      setCount(0);
    }
  }, []);

  useEffect(() => {
    fetchCount();
    const t = setInterval(fetchCount, 60_000);
    return () => clearInterval(t);
  }, [fetchCount]);

  // Hide on public routes / login screen
  const path = location.pathname;
  if (!isAdmin || count === 0) return null;
  if (path === "/" || path.startsWith("/login") || path.startsWith("/register")) return null;
  // Avoid showing while the user is already on the User Management page
  if (path.startsWith("/user-management")) return null;

  return (
    <button
      onClick={() => navigate("/user-management?pending=1")}
      data-testid="pending-approvals-badge"
      title={`${count} user${count === 1 ? "" : "s"} awaiting approval`}
      className="fixed top-3 right-3 z-[60] flex items-center gap-2 bg-orange-600 hover:bg-orange-700 text-white pl-2.5 pr-3 py-1.5 rounded-full shadow-lg border border-orange-700 animate-pulse-slow"
      style={{ animation: "pulse-soft 1.6s ease-in-out infinite" }}
    >
      <UserPlus className="w-4 h-4" />
      <span className="font-semibold text-sm tabular-nums">{count}</span>
      <span className="text-[11px] uppercase tracking-wide opacity-90 hidden sm:inline">
        Pending
      </span>
      <style>{`
        @keyframes pulse-soft {
          0%, 100% { box-shadow: 0 0 0 0 rgba(234, 88, 12, 0.55); }
          50%       { box-shadow: 0 0 0 8px rgba(234, 88, 12, 0); }
        }
      `}</style>
    </button>
  );
}

export default PendingApprovalsBadge;
